import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom"; // Import useParams from React Router
import { useFormik } from "formik";
import * as Yup from "yup";
import { showSuccess, showError } from "toast/toast";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import { Row } from "@tanstack/react-table";
import { Product } from "types";
import { Order, Agent, Address, Addresses } from "types";
import { format } from 'date-fns';
import { Table as MuiTable, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import { StatusBadge } from "components/Productlocationstatus";
import { InstallationStatusBadge } from "components/Installationstatus";
import { SalesOrderStatusBadge } from "components/SalesOrderstatus";
import ResponsiveDataTable from "components/TableandMobilecomponent";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import PaginationControlsView from "components/PaginationViewtables";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import EntityProfileViewCard from "components/EntityProfileViewCard";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  createColumnHelper,
} from "@tanstack/react-table";
import { addressLine1Validation, cityValidation, countryValidation, firstNameValidation, lastNameValidation, phoneValidation, stateValidation, zipcodeValidation } from "validationSchemas/common";
import DataTable from "components/DataTable";
import { getAuthCookie } from "../../utils/authCookies";
import { formatPaymentMethodLabel } from "../../utils/paymentMethodLabel";

interface AgentDetails {
  brokerageFirmAffiliation: string;
  managingBroker: string;
  agentID: string;
  licenseNumber: string;
  licenseExpiryDate: string;
};

interface FirmInfo {
  companyID: string;
  companyName: string;
  address: Address;
  specialities: string;
  regionsServed: string;
}

type AgentProduct = {
  product_id: string;
  _id: string;
  agent_id: string;
  agent_name: string;
  product: Product;
  created_by: { id: string; name: string };

};

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const agentAddressesColumnHelper = createColumnHelper<Addresses>();
const agentInventoryColumnHelper = createColumnHelper<AgentProduct>();
const agentOrdersColumnHelper = createColumnHelper<Order>();

const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
type SelectionState = { [productId: string]: string };

// Image handling constants
const HEX24 = /^[a-f0-9]{24}$/i;
const ABS_URL = /^https?:\/\//i;
const isHexId = (s?: string) => !!(s && HEX24.test(s.trim()));

type DeleteModalState = {
  open: boolean;
  id: string | null;
  name: string;
  type: "address" | "inventory" | null;
  meta?: {
    recordId?: string;
  };
};

const SignageViewAgent = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const currentLocation = useLocation();
  const [agent, setAgent] = useState<Agent | null>(null);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [error, setError] = useState<string | null>(null);

  // Determine the base route based on current location
  const isSignageTeammember = currentLocation.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressObjects, setAddressObjects] = useState<Addresses[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    open: false,
    id: null,
    name: "",
    type: null,
  });
  const [address, setAddress] = useState("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [agentProducts, setAgentProducts] = useState<AgentProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<AgentProduct[]>([]);
  const [noteSelections, setNoteSelections] = useState<SelectionState>({});

  const [totalRows, setTotalRows] = useState(0);
  // const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [orders, setOrders] = useState<Order[]>([]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });
  const navigate = useNavigate();
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);

  const [activeTab, setActiveTab] = useState<string>(window.location.hash || "#addresses");

  const [agentAddressesGlobalFilter, setAgentAddressesGlobalFilter] = useState("");
  const [agentAddressesRowCount, setAgentAddressesRowCount] = useState(0);
  const [agentAddressesSearchInput, setAgentAddressesSearchInput] = useState<string>(agentAddressesGlobalFilter ?? "");
  const [agentAddressesPagination, setAgentAddressesPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [agentInventoryGlobalFilter, setAgentInventoryGlobalFilter] = useState("");
  const [agentInventoryRowCount, setAgentInventoryRowCount] = useState(0);
  const [agentInventorySearchInput, setAgentInventorySearchInput] = useState<string>(agentInventoryGlobalFilter ?? "");
  const [agentInventoryPagination, setAgentInventoryPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [agentOrdersGlobalFilter, setAgentOrdersGlobalFilter] = useState("");
  const [agentOrdersRowCount, setAgentOrdersRowCount] = useState(0);
  const [agentOrdersSearchInput, setAgentOrdersSearchInput] = useState<string>(agentOrdersGlobalFilter ?? "");
  const [agentOrdersPagination, setAgentOrdersPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const location = useLocation();
  useEffect(() => {
    const fetchAgentData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${agentId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch agent data");
        }
        const data = await response.json();
        if (data && data.firmDetails && data.firmDetails.address) {
          setAgent(data);

          // setUserModules(data.modulePermissions);

        } else {
          console.error("Incomplete data", data);
        }
      } catch (error) {
        console.error("Error fetching agent data:", error);
      }
    };

    if (agentId) {
      fetchAgentData();
    }
  }, [agentId]);

  const performDeleteAddress = async (addressId: string) => {
    setDeleting(addressId); // Set the license being deleted

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/addresses/${addressId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        const message = result?.error || result?.message || "Failed to save data";
        console.error("API Error:", message);
        showError(message);
        throw new Error(message);
      }


      showSuccess("Address deleted successfully!");
      // Update the state by removing the deleted license
      setAddressObjects((prev) =>
        prev.filter((address) => address._id !== addressId)
      );
    } catch (err) {
      showError("Failed to delete the address.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
    } finally {
      setDeleting(null); // Clear the deleting state
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      label_of_address: "",
      firstName: "",
      lastName: "",
      company: "",
      addressLine1: "",
      addressLine2: "",
      country: "",
      county: "",
      city: "",
      state: "",
      zipcode: "",
      phone: "",
      cityCode: "",
      stateCode: "",
      countryCode: "",
      latitude: 0,
      longitude: 0,
    },
    validationSchema: Yup.object({
      label_of_address: Yup.string().required("Label of address is required"),
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      addressLine1: addressLine1Validation,
      city: cityValidation,
      state: stateValidation,
      zipcode: zipcodeValidation,
      country: countryValidation,
      phone: phoneValidation,
    }),
    onSubmit: async (values) => {
      setFormSubmitting(true);

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/addresses`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            user_id: userId,
            label: values.label_of_address,
            first_name: values.firstName,
            last_name: values.lastName,
            company: values.company,
            address_details: {
              addressLine1: values.addressLine1,
              addressLine2: values.addressLine2,
              city: values.county,
              state: values.state,
              zip: values.zipcode,
              country: values.country,
              phone: values.phone,
              cityCode: values.cityCode,
              stateCode: values.stateCode,
              countryCode: values.countryCode,
              latitude: values.latitude,
              longitude: values.longitude,
            },
          }),
        });

        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to save data";
          console.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        showSuccess("Address added successfully!");
        navigate(-1)
      } catch (error: any) {
        console.error(error);
      } finally {
        setFormSubmitting(false);
      }

    },
  });

  // const handleShowAddAddress = () => {
  //   setShowAddressForm(true);
  // };

  // const handleAddAddress = async (event: React.MouseEvent<HTMLButtonElement>) => {
  //   event.preventDefault();

  //   const errors = await formik.validateForm();

  //   if (Object.keys(errors).length > 0) {
  //     formik.setTouched(
  //       Object.keys(formik.values).reduce((acc, key) => {
  //         acc[key] = true;
  //         return acc;
  //       }, {} as Record<string, boolean>)
  //     );
  //     return; // Prevent submission if there are validation errors
  //   }

  //   try {
  //     console.log("Adding address:", formik.values);

  //     if (!agentId || !token) return;

  //     const response = await fetch(`${process.env.REACT_APP_API_URL}/addresses`, {
  //       method: "POST",
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify({
  //         user_id: userId,
  //         label: formik.values.label_of_address,
  //         first_name: formik.values.firstName,
  //         last_name: formik.values.lastName,
  //         company: formik.values.company,
  //         address_details: {
  //           addressLine1: formik.values.addressLine1,
  //           addressLine2: formik.values.addressLine2,
  //           city: formik.values.county,
  //           state: formik.values.state,
  //           zip: formik.values.zipcode,
  //           country: formik.values.country,
  //           phone: formik.values.phone,
  //           cityCode: formik.values.cityCode,
  //           stateCode: formik.values.stateCode,
  //           countryCode: formik.values.countryCode,
  //           latitude: formik.values.latitude,
  //           longitude: formik.values.longitude,
  //         },
  //       }),
  //     });

  //     if (!response.ok) throw new Error("Failed to add address");

  //     const data = await response.json();
  //     setAddressObjects([...addressObjects, data]);
  //   } catch (error) {
  //     console.error("Error adding address:", error);
  //   }
  // };


  // const handlePlaceChanged = () => {
  //   if (autocompleteRef.current) {
  //     const place = autocompleteRef.current.getPlace();
  //     console.log("Place changed:", place);

  //     const addressComponents = place.address_components;
  //     console.log(addressComponents);

  //     if (addressComponents) {
  //       const getComponent = (type: string) => {
  //         const component = addressComponents?.find(c => c.types.includes(type));
  //         return component ? { long_name: component.long_name, short_name: component.short_name } : { long_name: '', short_name: '' };
  //       };

  //       const streetNumber = getComponent('street_number').long_name;
  //       const route = getComponent('route').long_name;

  //       const cityObj = getComponent('locality') || getComponent('administrative_area_level_2');
  //       const stateObj = getComponent('administrative_area_level_1');
  //       const postalCode = getComponent('postal_code').long_name;
  //       const countryObj = getComponent('country');


  //       const street = `${streetNumber} ${route}`.trim();

  //       const latitude = place.geometry?.location?.lat();
  //       const longitude = place.geometry?.location?.lng();

  //       console.log("Latitude:", latitude, "Longitude:", longitude);

  //       // Update form values
  //       formik.setFieldValue('addressLine1', street);
  //       formik.setFieldValue('city', cityObj.long_name);
  //       formik.setFieldValue('cityCode', cityObj.short_name); // <-- Add this
  //       formik.setFieldValue('state', stateObj.long_name);
  //       formik.setFieldValue('stateCode', stateObj.short_name); // <-- Add this
  //       formik.setFieldValue('zipcode', postalCode);
  //       formik.setFieldValue('country', countryObj.long_name);
  //       formik.setFieldValue('countryCode', countryObj.short_name);

  //       if (latitude && longitude) {
  //         formik.setFieldValue('latitude', latitude);
  //         formik.setFieldValue('longitude', longitude);
  //       }
  //     }
  //   }
  // };

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const response = await fetch(
          // `${process.env.REACT_APP_API_URL}/office/${userId}/inventory/products`,
          `${process.env.REACT_APP_API_URL}/addresses/user/${agentId}/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: agentAddressesPagination.pageIndex + 1,
              limit: agentAddressesPagination.pageSize,
              search: agentAddressesGlobalFilter,
              filters: {

              },
              sort: {
                field: "created_at",
                order: "desc"
              },
              // fields: ["firstName", "lastName", "email", "jobTitle", "warehouses", "status", "created_at"]
            })
          }
        );

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const result = await response.json();
        setAddressObjects(result?.data ?? []);
        setAgentAddressesRowCount(result?.total ?? 0);

      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, [token, userId, agentAddressesGlobalFilter, agentAddressesPagination]);


  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(
          // `${process.env.REACT_APP_API_URL}/office/${userId}/inventory/products`,
          `${process.env.REACT_APP_API_URL}/agent/${agentId}/inventory/products/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: agentInventoryPagination.pageIndex + 1,
              limit: agentInventoryPagination.pageSize,
              search: agentInventoryGlobalFilter,
              filters: {

              },
              sort: {
                field: "created_at",
                order: "desc"
              },
              // fields: ["firstName", "lastName", "email", "jobTitle", "warehouses", "status", "created_at"]
            })
          }
        );

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const result = await response.json();
        // console.log("Fetched data:", result);

        // if (result && Array.isArray(result?.data)) {
        setAgentProducts(result?.data);
        setAgentInventoryRowCount(result?.total);
        // setFilteredProducts(result?.data);
        // } else {
        //   console.error("Unexpected API response structure:", data);
        //   setAgentProducts([]);
        //   // setFilteredProducts([]);
        // }
      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [token, userId, agentInventoryGlobalFilter, agentInventoryPagination]);

  // Fetch orders data
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/orders/agent/${agentId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch orders data");
        }

        const data = await response.json();
        // console.log("Fetched orders Data:", data);
        if (data) {
          setOrders(data);
        }
      } catch (err) {
        console.error("Error fetching orders data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) fetchOrders();
  }, [userId, token]);

  const performDeleteAgentProduct = async (productId: string, recordId?: string) => {
    if (!agentId) return;
    setDeleting(productId); // Set the license being deleted

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/agent/${encodeURIComponent(agentId)}/inventory/${encodeURIComponent(productId)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        const message = result?.error || result?.message || "Failed to save data";
        console.error("API Error:", message);
        showError(message);
        throw new Error(message);
      }
      showSuccess("Product deleted successfully!");
      setAgentProducts((prev) =>
        prev?.filter((product) =>
          recordId ? product._id !== recordId : product._id !== productId && product.product_id !== productId
        )
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.id || !deleteModal.type) {
      setDeleteModal((prev) => ({ ...prev, open: false }));
      return;
    }

    try {
      if (deleteModal.type === "address") {
        await performDeleteAddress(deleteModal.id);
      } else if (deleteModal.type === "inventory") {
        await performDeleteAgentProduct(deleteModal.id, deleteModal.meta?.recordId);
      }
    } finally {
      setDeleteModal({
        open: false,
        id: null,
        name: "",
        type: null,
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      open: false,
      id: null,
      name: "",
      type: null,
    });
  };

  const handleAddressDeleteClick = (address: Addresses) => {
    setDeleteModal({
      open: true,
      id: address._id,
      name: address.label || "Address",
      type: "address",
    });
  };

  const handleInventoryDeleteClick = (agentProduct: AgentProduct) => {
    const productId = String(agentProduct.product_id || "").trim();
    if (!productId) {
      showError("Missing product id for delete.");
      return;
    }
    setDeleteModal({
      open: true,
      id: productId,
      name: agentProduct.product?.name || "Product",
      type: "inventory",
      meta: { recordId: agentProduct._id },
    });
  };

  // Agent Addresses
  const columns = useMemo(() => {
    return [
      agentAddressesColumnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No-V" >S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No-V">
            {agentAddressesPagination.pageIndex * agentAddressesPagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),
      agentAddressesColumnHelper.display({
        id: "label",
        header: () => <div className="th-text S-No-V1">Label of Address</div>,
        cell: (info) => {
          const address = info.row.original;
          return (
            <div className="text-sm S-No-V1">{address.label}</div>
          );
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      agentAddressesColumnHelper.accessor("address_details.addressLine1", {
        header: () => <div className="th-text">Address line 1</div>,
        cell: (info) => (
          <div className="text-sm">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      agentAddressesColumnHelper.accessor("address_details.city", {
        header: () => <div className="th-text">City</div>,
        cell: (info) => (
          <div className="text-sm">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      agentAddressesColumnHelper.accessor("address_details.state", {
        header: () => <div className="th-text">State</div>,
        cell: (info) => (
          <div className="text-sm">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      agentAddressesColumnHelper.accessor("address_details.country", {
        header: () => <div className="th-text">Country</div>,
        cell: (info) => (
          <div className="text-sm">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
      agentAddressesColumnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => {
          const address = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              <EditIconButton
                to={`${baseRoute}/agent/editAddress/${agentId}/${address._id}`}
                className="icon-square1"
                title="Edit"
              />

              <DeleteIconButton
                title="Delete"
                className="icon-square1 danger"
                onClick={() => handleAddressDeleteClick(address)}
                disabled={deleting === address._id}
                type="button"
                style={{ cursor: deleting === address._id ? "not-allowed" : "pointer" }}
              />

            </div>
          );
        },
      }),
    ];
  }, [deleting, agentAddressesPagination, agentId, handleAddressDeleteClick]);


  const table = useReactTable({
    data: addressObjects,
    columns,
    pageCount: Math.ceil(agentAddressesRowCount / agentAddressesPagination.pageSize),
    state: {
      pagination: agentAddressesPagination,
      globalFilter: agentAddressesGlobalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setAgentAddressesPagination,
    onGlobalFilterChange: setAgentAddressesGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  const handleUnlink = async (productId: string, agentId: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/linked-installer/agent/${agentId}/product/${productId}/approve-unlink`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();
      if (!response.ok) {
        showError(data?.error || "Failed to unlink product.");
        return;
      }

      showSuccess(data.message || "Product Unlinked successfully!");
      navigate(0);
    } catch (error) {
      console.error("Error unlinking product:", error);
    }
  };

  const handleNoteChange = (productId: string, value: string) => {
    setNoteSelections((prev) => ({ ...prev, [productId]: value }));
  };

  // Image URL map setup for agent products
  const allImageIds = useMemo(() => {
    const ids: string[] = [];
    for (const row of (agentProducts || [])) {
      const id = (row?.product as any)?.images?.[0]?.url as string;
      if (isHexId(id)) ids.push(id.trim());
    }
    return Array.from(new Set(ids));
  }, [agentProducts]);

  // hook expects: [{ product: { variants: [{ images: [{ url }] }] } }]
  const agentProductsDataForMap = useMemo(() => {
    if (allImageIds.length === 0) return [];
    const images = allImageIds.map(url => ({ url }));
    return [
      { product: { variants: [{ images }] } }
    ];
  }, [allImageIds]);

  const agentProductsImageUrlMap = useVariantImageUrlMap(agentProductsDataForMap, token);

  // Agent Inventory

  const agentInventoryColumns = useMemo<ColumnDef<AgentProduct, any>[]>(() => {
    return [
      agentInventoryColumnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No-V">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No-V">
            {agentInventoryPagination.pageIndex * agentInventoryPagination.pageSize + info.row.index + 1}
          </div>
        ),
        size: 50,
        enableResizing: true,
      }),
      agentInventoryColumnHelper.display({
        id: "image",
        header: () => (
          <div className="th-text S-No-V1">
            Image
          </div>
        ),
        cell: info => {
          const raw = (info.row.original?.product as any)?.images?.[0]?.url as string | undefined;
          let url = "";

          if (raw) {
            if (ABS_URL.test(raw)) {
              // already a full URL
              url = raw;
            } else if (isHexId(raw)) {
              // hex id -> resolve via presigned map / helper
              const map: Record<string, string> = (agentProductsImageUrlMap as any) || {};
              url = map[raw] || getImageSrc(raw, map) || "";
            } else {
              // legacy relative path
              const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
              const path = raw.replace(/^\/+/, "");
              url = `${base}/${encodeURI(path)}`;
            }
          }

          return (
            <div className="text-sm S-No-V1">
              {url ? (
                <img src={url} alt="Product" style={{ width: 40, height: 40, objectFit: "contain", borderRadius: 4 }} />
              ) : (
                <span>No Image</span>
              )}
            </div>
          );
        },
        enableGlobalFilter: false,
        size: 80,
        enableResizing: true,
      }),
      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) => row.agent_name,
        {
          id: "agent_name",
          header: () => (
            <div className="th-text">
              Agent Name
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue() || "-"}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),

      agentInventoryColumnHelper.accessor(
        (row: any) => row.agent_email,
        {
          id: "agent_email",
          header: () => (
            <div className="th-text">
              Agent Email
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue() || "-"}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) => row.product.name,
        {
          id: "name",
          header: () => (
            <div className="th-text">
              Product Name
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              {info.getValue()}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      agentInventoryColumnHelper.accessor((row) => row.product.sku || "-", {
        id: "sku",
        header: () => <div className="th-text">SKU</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        size: 90,
        enableResizing: true,
      }),
      agentInventoryColumnHelper.accessor("product.category", {
        header: () => <div className="th-text">Category</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        size: 90,
        enableResizing: true,
      }),
      agentInventoryColumnHelper.accessor((row) => row.product?.variants?.[0]?.inventory?.[0]?.on_hand, {
        id: "on_hand",
        header: () => <div className="th-text">Qty</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        size: 40,
        enableResizing: true,
      }),
      agentInventoryColumnHelper.accessor("product.item_condition", {
        id: "item_condition",
        header: () => <div className="th-text">Condition</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        size: 90,
        enableResizing: true,
      }),
      // agentInventoryColumnHelper.accessor((row) => row.created_by?.name || "-", {
      //   id: "linked_by",
      //   header: () => <div className="th-text">Linked By</div>,
      //   cell: (info) => <div className="text-sm">{info.getValue()}</div>,
      //   filterFn: fuzzyTextFilter,
      //   enableGlobalFilter: true,
      //   size: 120,
      //   enableResizing: true,
      // }),
      agentInventoryColumnHelper.accessor((row: AgentProduct) =>
        row.product.product_location_status?.replace(/_/g, " ") || "-", {
        id: "product_location_status",
        header: () => <div className="th-text">Handover Method</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
        size: 180, // larger size for wide text
        enableResizing: true,
      }),


      // --- Supplier Actions ---
      // agentInventoryColumnHelper.display({
      //   id: "SupplierActions",
      //   header: () => (
      //     <div className="th-text">
      //       Supplier Actions
      //     </div>
      //   ),
      //   cell: info => {
      //     const p = info.row.original;
      //     console.log(p, "p");
      //     return (
      //       <div className="text-sm">
      //         {p.product?.linked_mode === "unlock_pending" ? (
      //           <>
      //             <p className="text-danger mb-0 fw-bold">Unlink Requested</p>
      //             <button
      //               className="btn btn-danger btn-sm mb-2"
      //               onClick={() => handleUnlink(p.product_id, p.agent_id)}
      //             >
      //               Approve Unlink
      //             </button>
      //             <textarea
      //               className="form-control"
      //               placeholder="Add Reason..."
      //               value={noteSelections[p.product_id] ?? ""}
      //               onChange={e => handleNoteChange(p.product_id, e.target.value)}
      //             />
      //           </>
      //         ) : (
      //           "---"
      //         )}
      //       </div>
      //     );
      //   },
      //   enableGlobalFilter: false,
      // }),
      // agentInventoryColumnHelper.accessor(
      //   (row: any) => row.linked_installer_details?.supplier_name || "",
      //   {
      //     id: "linked_installer_details.supplier_name",
      //     header: () => <div className="th-text">Linked Supplier</div>,
      //     cell: (info) => <div className="text-sm">{info.getValue()}</div>,
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //   }),
      // agentInventoryColumnHelper.accessor(
      //   (row: AgentProduct) => (
      //     <span className="fw-bold text-capitalize text-info">
      //       {row?.product?.current_status &&
      //         row.product.current_status !== "none"
      //         ? row.product.current_status.replace(/_/g, " ") : "-"}
      //     </span>),
      //   {
      //     id: "current_status",
      //     header: () => <div className="th-text">Product location status</div>,
      //    cell: info => (
      //       <div className="text-sm">
      //         <StatusBadge status={info.getValue()} />
      //       </div>
      //     ),
      //     filterFn: fuzzyTextFilter,
      //     enableGlobalFilter: true,
      //     size: 180, // larger size for wide text
      //     enableResizing: true,
      //   }
      // ),
      agentInventoryColumnHelper.accessor(
        (row: AgentProduct) =>
          row.product.current_status?.replace(/_/g, " ") || "-",
        {
          id: "current_status",
          header: () => (
            <div className="th-text">
              Product location status
            </div>
          ),
          cell: info => (
            <div className="text-sm">
              <StatusBadge status={info.getValue()} />
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      agentInventoryColumnHelper.accessor((row: AgentProduct) => (
        <span className="text-success fw-bold">
          {row?.product?.linked_mode === "unlock_pending"
            ? "Unlink Pending"
            : row?.product?.locked_status === "unlocked"
              ? ""
              : "Linked"}
        </span>),
        {
          id: "linked_status",
          header: () => <div className="th-text ">Linked Status</div>,
          cell: (info) => <div className="text-sm linked-status">{info.getValue()}</div>,
          enableGlobalFilter: true,
          size: 100, // larger size for wide text
          enableResizing: true,
        }),
      agentInventoryColumnHelper.display({  
        id: "actions",
        header: () => <div className="th-text ">Actions</div>,
        cell: (info) => {
          const agentproduct = info.row.original;
          const ownerId = String(agentproduct.agent_id || agentId || "").trim();
          const productId = String(agentproduct.product_id || "").trim();

          return (
            <div className="d-flex gap-2 justify-content-center table-cells">

              <ViewIconButton
                to={`${baseRoute}/editAgentProduct/${ownerId}/${productId}`}
                className="btn action-btn " title="View"
              />

              <EditIconButton
                to={`${baseRoute}/editAgentProduct/${ownerId}/${productId}`}
                className="icon-square1"
                title="Edit"
              />
              <DeleteIconButton
                onClick={() => handleInventoryDeleteClick(agentproduct)}
                title="Delete"
                className="icon-square1 danger"
                disabled={deleting === productId || !productId}
              />
            </div>
          );
        },
      }),
    ];
  }, [agentInventoryPagination, deleting, handleInventoryDeleteClick, agentProductsImageUrlMap, agentId, baseRoute]);


  const agentInventoryTable = useReactTable({
    data: agentProducts ?? [],
    columns: agentInventoryColumns,
    pageCount: Math.ceil(agentInventoryRowCount / agentInventoryPagination.pageSize),
    state: {
      pagination: agentInventoryPagination,
      globalFilter: agentInventoryGlobalFilter,
    },
    defaultColumn: {
      size: 150, // default size for columns without size
      enableResizing: true,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setAgentInventoryPagination,
    onGlobalFilterChange: setAgentInventoryGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const agentInventoryPageCount = agentInventoryTable.getPageCount();
  const agentInventoryCurrentPage = agentInventoryTable.getState().pagination.pageIndex;
  const displayOrDash = (value: string | number | null | undefined): string | number => {
    return value ? value : "---";
  };
  useEffect(() => {
    const handleHashChange = () => {
      setActiveTab(window.location.hash || "#addresses");
    };

    window.addEventListener("hashchange", handleHashChange);
    handleHashChange(); // handle initial load

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Orders table (columns + instance)
  // Make sure agentOrdersColumnHelper is already created as in your file.
  const ordersColumns: ColumnDef<Order, any>[] = useMemo(() => [
    agentOrdersColumnHelper.display({
      id: "serial",
      header: () => <div className="th-text S-No-V">S.No.</div>,
      cell: (info) => (
        <div className="text-sm S-No-V">
          {(ordersTable?.getState().pagination.pageIndex ?? 0) * (ordersTable?.getState().pagination.pageSize ?? 10)
            + info.row.index + 1}
        </div>
      ),
    }),

    agentOrdersColumnHelper.accessor((row) => (row as any)?.order_number ?? (row as any)?.order_no ?? "---", {
      id: "order_no",
      header: () => <div className="th-text S-No-V1">Order ID</div>,
      cell: (info) => <div className="text-sm S-No-V1">{info.getValue()}</div>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),

    agentOrdersColumnHelper.accessor((row) => (row as any)?.customer_name ?? "---", {
      id: "customer_name",
      header: () => <div className="th-text">Customer</div>,
      cell: (info) => <div className="text-sm">{info.getValue()}</div>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),

    agentOrdersColumnHelper.accessor((row) => (row as any)?.items?.[0]?.product_name ?? "---", {
      id: "product_name",
      header: () => <div className="th-text">Product</div>,
      cell: (info) => <div className="text-sm">{info.getValue()}</div>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),

    agentOrdersColumnHelper.accessor((row) => (row as any)?.created_at ?? "", {
      id: "created_at",
      header: () => <div className="th-text">Order Date</div>,
      cell: (info) => {
        const v = info.getValue() as string;
        let out = "—";
        try { out = v ? format(new Date(v), "MMM d, yyyy") : "—"; } catch { }
        return <div className="text-sm">{out}</div>;
      },
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),

    agentOrdersColumnHelper.accessor((row) => (row as any)?.total_amount ?? "---", {
      id: "amount",
      header: () => <div className="th-text">Amount</div>,
      cell: (info) => <div className="text-sm">{info.getValue()}</div>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),

    agentOrdersColumnHelper.accessor((row) => formatPaymentMethodLabel((row as any)?.payment_method), {
      id: "payment_method",
      header: () => <div className="th-text">Payment Method</div>,
      cell: (info) => <div className="text-sm">{info.getValue()}</div>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),

    agentOrdersColumnHelper.accessor((row) => (row as any)?.status ?? (row as any)?.order_status ?? "---", {
      id: "status",
      header: () => <div className="th-text">Delivery Status</div>,
      cell: (info) => {
        const orderStatus =
          (info.row.original as any)?.status ??
          (info.row.original as any)?.order_status ??
          (info.getValue?.() as string) ??
          "---";

        return (
          <div className="text-sm">
            <SalesOrderStatusBadge type="order" value={orderStatus} />
          </div>
        );
      },
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),

    agentOrdersColumnHelper.display({
      id: "actions",
      header: () => <div className="th-text">Action</div>,
      cell: (info) => {
        const row = info.row.original as any;
        const id = row?.id ?? row?._id;

        return (
          <ul className="list-inline hstack gap-2 mb-0">
            <li className="list-inline-item">
              <ViewIconButton
                to={`${baseRoute}/orderDetails/${id}`}
                title="View"
                ariaLabel="View order"
              />
            </li>
          </ul>
        );
      },

    }),
  ], [orders]);

  // Client-side pagination and filtering for Orders
  const ordersTable = useReactTable({
    data: orders ?? [],
    columns: ordersColumns,
    state: {
      pagination: agentOrdersPagination,
      globalFilter: agentOrdersGlobalFilter,
    },
    onPaginationChange: setAgentOrdersPagination,
    onGlobalFilterChange: setAgentOrdersGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // useEffect(() => {
  //   // Set tab from location hash or from location.state.hash
  //   const fromHash = location.hash || location.state?.hash;
  //   if (fromHash && ["#addresses", "#inventory", "#orders"].includes(fromHash)) {
  //     setActiveTab(fromHash);
  //   }
  // }, [location]);
  const TAB_TABLES: Record<string, { table: any; name: string }> = {
    "#addresses": { table: table, name: "Team Members" },
    "#inventory": { table: agentInventoryTable, name: "inventory" },
    "#orders": { table: ordersTable, name: "orders" },
  };

  const activeEntry = TAB_TABLES[activeTab];

  // Build a tiny payload just for presigning this single image id
  const dataForMap = React.useMemo(() => {
    const id = (agent?.profilePic || "").trim();
    return id ? [{ product: { variants: [{ images: [{ url: id }] }] } }] : [];
  }, [agent?.profilePic]);

  // Get the presigned URL map
  const imageUrlMap = useVariantImageUrlMap(dataForMap, token || null);

  // Resolve final avatar URL from id (or fall back if needed)
  const avatarUrl: string | undefined =
    getImageSrc(agent?.profilePic, imageUrlMap) ||
    (typeof agent?.profilePic === "string" && agent.profilePic.startsWith("http")
      ? agent.profilePic
      : undefined);

  // Initials fallback (what you already show as “SF”)
  const initialsText =
    `${(agent?.firstName ?? "").slice(0, 1)}${(agent?.lastName ?? "").slice(0, 1)}`.toUpperCase() || "—";
  return (
    <div className="content">
      <div className="container py-3">
        <div className="container mb-3">
          {/* <Link to={`${baseRoute}/agents`} className="btn btn-light d-inline-flex align-items-center gap-2 br-8 px-3 mb-3">
            <i className="ri-arrow-left-line" />
            Back
          </Link> */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn btn-light d-inline-flex align-items-center gap-2 br-8 px-3 mb-3"
          >
            <i className="ri-arrow-left-line" />
            Back
          </button>
        </div>

        <EntityProfileViewCard
          name={[agent?.firstName, agent?.lastName].filter(Boolean).join(" ")}
          subtitle="Agent Details"
          status={agent?.status}
          editHref={`${baseRoute}/agent/${agentId}`}
          avatarUrl={avatarUrl}
          initials={initialsText}
          fields={[
            { label: "First Name", value: agent?.firstName },
            { label: "Last Name", value: agent?.lastName },
            { label: "Phone", value: agent?.phone1 },
            {
              label: "Email",
              value: agent?.email ? (
                <a href={`mailto:${agent.email}`} className="re-profile-card__link">
                  {agent.email}
                </a>
              ) : undefined,
            },
            { label: "Role", value: "Agent" },
            { label: "Brokerage Firm", value: agent?.agentDetails?.brokerageFirmAffiliation },
            // { label: "Agent ID", value: agent?.agentDetails?.agentID },
          ]}
        />

      </div>
      <div className="container py-3">
        {/* Tabs (same soft style) */}
        <div className="tab-content text-muted box-shadow-datatables shadow-border-datatables">
          <ul className="nav nav-tabs ms-3 mt-4" role="tablist">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link-soft text-sm ${activeTab === "#addresses" ? "active" : ""}`}
                role="tab"
                onClick={() => setActiveTab("#addresses")}
              >
                <i className="bi bi-geo-alt me-2" />
                Addresses
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link-soft text-sm ${activeTab === "#inventory" ? "active" : ""}`}
                role="tab"
                onClick={() => setActiveTab("#inventory")}
              >
                <i className="bi bi-box-seam me-2" />
                Inventory
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link-soft text-sm ${activeTab === "#orders" ? "active" : ""}`}
                role="tab"
                onClick={() => setActiveTab("#orders")}
              >
                <i className="bi bi-receipt me-2" />
                Orders
              </button>
            </li>
          </ul>

          {/* ================= ADDRESSES ================= */}
          <div className={`tab-pane ${activeTab === "#addresses" ? "active" : ""}`} id="addresses" role="tabpanel">
            <div className="panel shadow-border-datatables box-shadow-datatables m-4">
              {/* Title */}
              <div className="panel-head px-4 py-3 border-bottom">
                <h5 className="m-0 view-cards">Address Management</h5>
              </div>

              {/* Toolbar: search left + add button right */}
              <div className="panel-toolbar px-4 py-3 border-bottom d-flex align-items-center justify-content-between gap-3 flex-wrap">
                <div className="input-group tm-search">
                  <span className="input-group-text"><i className="bi bi-search" /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by Label Name, City, Contact Person"
                    value={agentAddressesSearchInput}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setAgentAddressesSearchInput(e.target.value)}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === "Enter") {
                        setAgentAddressesGlobalFilter(agentAddressesSearchInput);
                      }
                    }}

                  />
                </div>

                <Link to={`${baseRoute}/agent/addAddress/${agentId}`} className="btn btn-primary table-addbutton  d-inline-flex align-items-center">
                  <i className="bi bi-plus-lg me-2" />
                  Add Address
                </Link>
              </div>

              {/* Desktop table */}
              <div className="rdt-desktop">
                {table?.getRowModel().rows.length ? (
                  <>
                    <div className="panel-table">
                      <div className="table-wrap" style={{ overflowX: "auto" }}>
                        <DataTable table={table} />
                      </div>
                    </div>
                    {activeEntry && (
                      <PaginationControlsView table={activeEntry.table} />
                    )}

                    {/* Pager inside card (small) */}
                    {/* <div className="brokerage-ui">
                      <div className="panel-pager border-top px-4 py-3 d-flex align-items-center justify-content-between">
                        <span>
                          Page {Math.min((table.getState().pagination.pageIndex ?? 0) + 1, table.getPageCount?.() ?? 1)} of {table.getPageCount?.() ?? 1}
                        </span>

                        <div className="d-flex align-items-center gap-3 flex-wrap">
                          <div className="d-flex align-items-center gap-2">
                            <span>Rows per page:</span>
                            <select
                              className="form-select pageSizeSelect1"
                              value={table.getState().pagination.pageSize}
                              onChange={(e) => { table.setPageSize(Number(e.target.value)); table.setPageIndex(0); }}
                            >
                              {[5, 10, 20, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>

                          <div className="pager-controls">
                            <button className="pager-chevron" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage?.()}>
                              <i className="bi bi-chevron-left" />
                            </button>
                            <button className="pager-chip active">
                              {(table.getState().pagination.pageIndex ?? 0) + 1}
                            </button>
                            <button className="pager-chevron" onClick={() => table.nextPage()} disabled={!table.getCanNextPage?.()}>
                              <i className="bi bi-chevron-right" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div> */}
                  </>
                ) : (
                  <div className="px-4 py-4 text-center text-muted" style={{ fontSize: "1.05rem" }}>
                    No addresses found
                  </div>
                )}
              </div>

              {/* Mobile cards */}
              <div className="rdt-mobile">
                <MobileCardsOnly
                  table={table}
                  idColId="label"
                  titleColId="addressLine1"
                  actionColId="actions"
                  omitMobile={["index"]}
                />
              </div>
            </div>
          </div>

          {/* ================= INVENTORY ================= */}
          <div className={`tab-pane ${activeTab === "#inventory" ? "active" : ""}`} id="inventory" role="tabpanel">
            <div className="panel shadow-border-datatables box-shadow-datatables m-4">
              <div className="panel-head px-4 py-3 border-bottom">
                <h5 className="m-0 view-cards">Agent Inventory</h5>
              </div>

              <div className="panel-toolbar px-4 py-3 border-bottom d-flex align-items-center justify-content-between gap-3 flex-wrap">
                <div className="input-group tm-search">
                  <span className="input-group-text"><i className="bi bi-search" /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by Agent Name, Product Name or SKU"
                    value={agentInventorySearchInput}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setAgentInventorySearchInput(e.target.value)}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === "Enter") {
                        setAgentInventoryGlobalFilter(agentInventorySearchInput)
                      }
                    }}
                  />
                </div>

                <Link to={`${baseRoute}/addAgentProduct/agent/${agentId}`} className="btn btn-primary table-addbutton  d-inline-flex align-items-center">
                  <i className="bi bi-plus-lg me-2" />
                  Add Agent Product
                </Link>
              </div>

              <div className="rdt-desktop">
                {agentInventoryTable?.getRowModel().rows.length ? (
                  <>
                    <div className="panel-table">
                      <div className="table-wrap" style={{ overflowX: "auto" }}>
                        <DataTable table={agentInventoryTable} />
                      </div>
                    </div>
                    {activeEntry && (
                      <PaginationControlsView table={activeEntry.table} />
                    )}
                  </>
                ) : (
                  <div className="px-4 py-4 text-center text-muted" style={{ fontSize: "1.05rem" }}>
                    No records found
                  </div>
                )}
              </div>

              <div className="rdt-mobile">
                <MobileCardsOnly
                  table={agentInventoryTable}
                  idColId="id"
                  titleColId="product_name"
                  actionColId="actions"
                />
              </div>
            </div>
          </div>

          {/* ================= ORDERS ================= */}
          <div className={`tab-pane ${activeTab === "#orders" ? "active" : ""}`} id="orders" role="tabpanel">
            <div className="panel shadow-border-datatables box-shadow-datatables m-4">
              <div className="panel-head px-4 py-3 border-bottom">
                <h5 className="m-0 view-cards">Orders</h5>
              </div>

              <div className="panel-toolbar px-4 py-3 border-bottom d-flex align-items-center justify-content-between gap-3 flex-wrap">
                <div className="input-group tm-search">
                  <span className="input-group-text"><i className="bi bi-search" /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search orders…"
                  //  value={ordersSearchInput}
                  //               onChange={(e: ChangeEvent<HTMLInputElement>) => setOrdersSearchInput(e.target.value)}
                  //               onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  //                 if (e.key === "Enter") {
                  //                   setOrdersGlobalFilter(searchInput);
                  //                 }
                  //               }}
                  />
                </div>

                {/* <Link to={`/signage/agent/addOrder/${agentId}`} className="btn btn-primary d-inline-flex align-items-center">
                  <i className="bi bi-plus-lg me-2" />
                  Add Order
                </Link> */}
              </div>

              <div className="rdt-desktop">
                {ordersTable?.getRowModel().rows.length ? (
                  <>
                    <div className="panel-table">
                      <div className="table-wrap" style={{ overflowX: "auto" }}>
                        <DataTable table={ordersTable} />
                      </div>
                    </div>

                    {activeEntry && (
                      <PaginationControlsView table={activeEntry.table} />
                    )}
                  </>
                ) : (
                  <div className="px-4 py-4 text-center text-muted" style={{ fontSize: "1.05rem" }}>
                    No orders found
                  </div>
                )}
              </div>

              <div className="rdt-mobile">
                <MobileCardsOnly
                  table={ordersTable}
                  idColId="order_no"
                  titleColId="product_name"
                  actionColId="actions"
                />
              </div>
            </div>
          </div>
          {/* styles (exact layout) */}
          <style>{`
      /* Tabs */
      .nav-tabs-soft { border-bottom:1px solid #e9eef5; gap:8px; overflow-x:auto; }
      .nav-link-soft { position:relative; border:0!important; background:transparent!important; color:#6b7280; padding:10px 14px; border-radius:8px; font-weight:500; display:inline-flex; align-items:center; white-space:nowrap; }
      .nav-link-soft:hover { color:#111827; }
      .nav-link-soft.active { color:#2563eb; }
      .nav-link-soft.active::after { content:""; position:absolute; left:10px; right:10px; bottom:-1px; height:3px; background:#2563eb; border-radius:3px; }

      /* Inner panel */
      .panel { border:1px solid #edf1f7; background:#fff; overflow:hidden; }
      .panel-head h5 { line-height:1.3; }
      .tm-search { width: clamp(240px, 28vw, 360px); }
      .tm-search .input-group-text { background:#fff; border-right:0; }
      .tm-search .form-control { border-left:0; border-color:#dfe5ef; height:40px; }
      .tm-search .input-group-text, .tm-search .form-control { border-radius:8px; }

      /* Table shell: sticky header + scrollbars (both axis) */
      // .panel-table .table-wrap { max-height: 500px; overflow: auto; }
      .panel-table .table-wrap table { margin-bottom:0; }
      .panel-table thead th { position: sticky; top: 0; z-index: 2; background: #f8fafc; }

      .panel-table .table-wrap::-webkit-scrollbar { height:10px; width:10px; }
      .panel-table .table-wrap::-webkit-scrollbar-thumb { background:#cfd7e6; border-radius:8px; }
      .panel-table .table-wrap::-webkit-scrollbar-track { background:#f3f6fb; }

      // /* Pager (inside panel) */
      // .pager-controls { display:flex; align-items:center; gap:10px; }
      // .pager-chevron { width:38px; height:38px; border-radius:8px; border:1px solid #e5e7eb; background:#fff; display:flex; align-items:center; justify-content:center; }
      // .pager-chevron:disabled { opacity:.5; cursor:not-allowed; }
      // .pager-chip { min-width:38px; height:38px; border-radius:8px; border:1px solid #e5e7eb; background:#fff; display:inline-flex; align-items:center; justify-content:center; font-weight:600; }
      // .pager-chip.active { background:#2563eb; color:#fff; border-color:#2563eb; }
      // .pageSizeSelect { width:92px; height:38px; }

      /* Desktop vs mobile */
      .rdt-desktop { display:block; }
      .rdt-mobile  { display:none; }
      @media (max-width: 1021px) {
        .rdt-desktop { display:none!important; }
        .rdt-mobile  { display:block!important; }
      }

      .card.shadow-sm, .shadow-sm { box-shadow:0 4px 14px rgba(20,29,56,.06) !important; }
    `}</style>
        </div>

        {/* keep your existing styles block in the page; it already targets these classes */}
      </div>


      <DeleteConfirmModal
        open={deleteModal.open}
        dynamicName={deleteModal.name || "Item"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );

};

export default SignageViewAgent;



// return (
//   <div className="content">
//     <div className="container mt-4 ">
//       <div className="bg-white rounded-3 shadow border p-4 position-relative">
//         <h2 className="text-center viewheaders mb-5">Agent Details</h2>
//         <div
//           className="position-absolute"
//           style={{ top: "16px", right: "16px" }}
//         >

//           <Link to={`/signage/edit-agent/${agentId}`} className="edit me-3" title="Edit">
//             <i className="ri-pencil-fill fs-20"></i>
//           </Link>
//         </div>

//         <div className="row gy-3">
//           <div className="col-md-4">
//             <p>
//               <strong className="fw-semibold viewcells">First Name:</strong>{" "}
//               {displayOrDash(agent?.firstName)}
//             </p>
//           </div>
//           <div className="col-md-4">
//             <p>
//               <strong className="fw-semibold viewcells">Last Name:</strong>{" "}
//               {displayOrDash(agent?.lastName)}
//             </p>
//           </div>
//           <div className="col-md-4">
//             <p>
//               <strong className="fw-semibold viewcells">Email:</strong>{" "}
//               {displayOrDash(agent?.email)}
//             </p>
//           </div>

//           <div className="col-md-4">
//             <p>
//               <strong className="fw-semibold viewcells">Phone:</strong>{" "}
//               {displayOrDash(agent?.phone1)}
//             </p>
//           </div>
//           <div className="col-md-4">
//             <p>
//               <strong className="fw-semibold viewcells">Role:</strong> Agent
//             </p>
//           </div>
//           <div className="col-md-4">
//             <p>
//               <strong className="fw-semibold viewcells">Location:</strong>{" "}
//               {[
//                 agent?.firmDetails?.address?.addressLine1,
//                 agent?.firmDetails?.address?.addressLine2,
//               ]
//                 .filter(Boolean)
//                 .join(", ") || "---"}
//               , {displayOrDash(agent?.firmDetails?.address?.city)},{" "}
//               {displayOrDash(agent?.firmDetails?.address?.state)},{" "}
//               {displayOrDash(agent?.firmDetails?.address?.zip)}
//             </p>
//           </div>

//           <div className="col-md-4">
//             <p>
//               <strong className="fw-semibold viewcells">Brokerage Firm Affiliation:</strong>{" "}
//               {displayOrDash(agent?.agentDetails?.brokerageFirmAffiliation)}
//             </p>
//           </div>
//           <div className="col-md-4">
//             <p>
//               <strong className="fw-semibold viewcells">Agent ID/Code:</strong>{" "}
//               {displayOrDash(agent?.agentDetails?.agentID)}
//             </p>
//           </div>
//           <div className="col-md-4">
//             <p>
//               <strong className="fw-semibold viewcells">Status:</strong>{" "}
//               {displayOrDash(agent?.status)}
//             </p>
//           </div>
//           <div className="row mb-3 rcust-margin-4">
//             <div className="col-md-12 d-flex justify-content-between">
//               <div></div>
//               <button
//                 className="btn btn-outline-secondary br-8 backbutton"
//                 type="button"
//                 onClick={() => navigate(-1)}
//               >
//                 Back
//               </button>
//             </div>
//           </div>
//         </div>


//         {/* Profile Picture Section (optional) */}
//         {/* <div className="mt-4 text-center">
//     <img
//       src={
//         agent?.profilePic
//           ? `https://dev.reelty.app/${agent?.profilePic}`
//           : "/assets/images/users/avatar-1.jpg"
//       }
//       alt="agent"
//       className="img-thumbnail rounded-circle"
//       style={{ width: 100, height: 100, objectFit: "cover" }}
//     />
//   </div> */}
//       </div>
//     </div>

//     <div className="row">
//       <div className="col-xxl-12 ">
//         {/* <div className="card">
//               <div className="card-body"> */}
//         <ul
//           className="nav nav-tabs nav-tabs-custom nav-success nav-justified mb-3"
//           role="tablist"
//         >
//           <li className="nav-item">
//             <a
//               className={`nav-link ${activeTab === "#addresses" ? "active" : ""}`}
//               data-bs-toggle="tab"
//               href="#addresses"
//               role="tab"
//               onClick={() => setActiveTab("#addresses")}
//             >
//               Addresses
//             </a>
//           </li>
//           <li className="nav-item">
//             <a
//               className={`nav-link ${activeTab === "#inventory" ? "active" : ""}`}
//               data-bs-toggle="tab"
//               href="#offices"
//               role="tab"
//               onClick={() => setActiveTab("#inventory")}
//             >
//               Inventory
//             </a>
//           </li>
//           <li className="nav-item">
//             <a
//               className={`nav-link ${activeTab === "#orders" ? "active" : ""}`}
//               data-bs-toggle="tab"
//               href="#offices"
//               role="tab"
//               onClick={() => setActiveTab("#orders")}
//             >
//               Orders
//             </a>
//           </li>
//         </ul>
//         <div className="tab-content text-muted">
//           <div className={`tab-pane ${activeTab === "#addresses" ? "active" : ""}`} id="addresses" role="tabpanel">
//             <div className="mb-3 d-flex flex-wrap justify-content-end">
//               <div className="input-group br-8" style={{ width: "200px" }}>
//                 <span className="input-group-text bg-white pe-0">
//                   <i className="ri-search-line"></i>
//                 </span>
//                 <input
//                   type="text"
//                   className="form-control border-start-0 me-2"

//                   placeholder="Search by Label Name, City, Contact Person"
//                   value={agentAddressesSearchInput}
//                   onChange={(e: ChangeEvent<HTMLInputElement>) => setAgentAddressesSearchInput(e.target.value)}
//                   onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
//                     if (e.key === "Enter") {
//                       setAgentAddressesGlobalFilter(agentAddressesSearchInput);
//                     }
//                   }}
//                   style={{ borderColor: "#cccccc", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
//                 />
//               </div>

//               <Link
//                 to={`/signage/agent/addAddress/${agentId}`}
//                 className="btn btn-primary ms-2"
//               >
//                 Add Address
//               </Link>
//             </div>

//             <DataTable table={table} />
//             <div className="d-flex justify-content-between align-items-center mt-3 pagination-blk">
//               <span>
//                 Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
//               </span>

//               <div className="pagination-container">
//                 {/* ◀️ Left */}
//                 <button
//                   className="pagination-arrow"
//                   onClick={() => table.previousPage()}
//                   disabled={!table.getCanPreviousPage()}
//                 >
//                   <i className="ri-arrow-left-circle-line fs-24"></i>
//                 </button>

//                 {/* 1 */}
//                 <button
//                   className={`pagination-circle${currentPage === 0 ? ' active' : ''}`}
//                   onClick={() => table.setPageIndex(0)}
//                 >
//                   1
//                 </button>

//                 {/* … before */}
//                 {currentPage > 1 && <span className="pagination-ellipsis">…</span>}

//                 {/* current */}
//                 {currentPage > 0 && currentPage < pageCount - 1 && (
//                   <button
//                     className="pagination-circle active"
//                     onClick={() => table.setPageIndex(currentPage)}
//                   >
//                     {currentPage + 1}
//                   </button>
//                 )}

//                 {/* … after */}
//                 {currentPage < pageCount - 2 && <span className="pagination-ellipsis">…</span>}

//                 {/* last */}
//                 {pageCount > 1 && (
//                   <button
//                     className={`pagination-circle${currentPage === pageCount - 1 ? ' active' : ''}`}
//                     onClick={() => table.setPageIndex(pageCount - 1)}
//                   >
//                     {pageCount}
//                   </button>
//                 )}

//                 {/* ▶️ Right */}
//                 <button
//                   className="pagination-arrow"
//                   onClick={() => table.nextPage()}
//                   disabled={!table.getCanNextPage()}
//                 >
//                   <i className="ri-arrow-right-circle-line fs-24"></i>
//                 </button>
//               </div>

//               {/* <div className="btn-group">
//                     <button
//                       className={`btn btn-sm ${agentInventoryCurrentPage === 0 ? 'disabled' : ''}`}
//                       onClick={() => agentInventoryTable.previousPage()}
//                       disabled={!agentInventoryTable.getCanPreviousPage()}
//                     >
//                       <i className="ri-arrow-left-s-line fs-20"></i>
//                     </button>
//                     <button
//                       className={`btn btn-sm ${agentInventoryCurrentPage === 0 ? 'btn-success' : 'btn-outline-secondary'}`}
//                       onClick={() => agentInventoryTable.setPageIndex(0)}
//                     >
//                       1
//                     </button>

//                     {agentInventoryCurrentPage > 1 && (
//                       <button className="btn btn-sm" disabled>
//                         ...
//                       </button>
//                     )}

//                     {agentInventoryCurrentPage !== 0 && agentInventoryCurrentPage !== agentInventoryPageCount - 1 && (
//                       <button
//                         className="btn btn-sm btn-success"
//                         onClick={() => agentInventoryTable.setPageIndex(agentInventoryCurrentPage)}
//                       >
//                         {agentInventoryCurrentPage + 1}
//                       </button>
//                     )}

//                     {agentInventoryCurrentPage < agentInventoryPageCount - 2 && (
//                       <button className="btn btn-sm" disabled>
//                         ...
//                       </button>
//                     )}

//                     {agentInventoryPageCount > 1 && (
//                       <button
//                         className={`btn btn-sm ${agentInventoryCurrentPage === agentInventoryPageCount - 1 ? 'btn-success' : 'btn-outline-secondary'}`}
//                         onClick={() => agentInventoryTable.setPageIndex(agentInventoryPageCount - 1)}
//                       >
//                         {agentInventoryPageCount}
//                       </button>
//                     )}

//                     <button
//                       className={`btn btn-sm ${agentInventoryCurrentPage === agentInventoryPageCount - 1 ? 'disabled' : ''}`}
//                       onClick={() => agentInventoryTable.nextPage()}
//                       disabled={!agentInventoryTable.getCanNextPage()}
//                     >
//                       <i className="ri-arrow-right-s-line fs-22"></i>
//                     </button>
//                   </div> */}

//               <div className="d-flex align-items-center gap-2">
//                 <label htmlFor="pageSizeSelectAgentInventory">Rows per page:</label>
//                 <select
//                   id="pageSizeSelectAgentInventory"
//                   className="form-select pageSizeSelect"
//                   value={table.getState().pagination.pageSize}
//                   onChange={(e) => {
//                     table.setPageSize(Number(e.target.value));
//                   }}
//                 >
//                   {[5, 10, 20, 50, 100].map((size) => (
//                     <option key={size} value={size}>
//                       {size}
//                     </option>
//                   ))}
//                 </select>
//               </div>
//             </div>
//           </div>
//           <div className={`tab-pane ${activeTab === "#inventory" ? "active" : ""}`} id="inventory" role="tabpanel">
//             <div className="dflex">
//               <div className="d-flex justify-content-end">
//                 <div className="col-md-6 d-flex gap-2 justify-content-end">
//                   <div className="input-group mb-3" style={{ width: "250px" }}>
//                     <span className="input-group-text bg-white pe-0">
//                       <i className="ri-search-line"></i>
//                     </span>
//                     <input
//                       type="text"
//                       className="search-blk form-control border-start-0 me-3"
//                       placeholder="Search by Agent Name, Product Name or SKU"
//                       value={agentInventorySearchInput}
//                       onChange={(e: ChangeEvent<HTMLInputElement>) => setAgentInventorySearchInput(e.target.value)}
//                       onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
//                         if (e.key === "Enter") {
//                           setAgentInventoryGlobalFilter(agentInventorySearchInput);
//                         }
//                       }}
//                       style={{ borderColor: "#cccccc", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
//                     />
//                   </div>

//                   {/* <ExportAsExcel
//                             data={exportRows}
//                             headers={[
//                               "Agent Name",
//                               "Product Name",
//                               "SKU",
//                               "Quantity",
//                               "Brand",
//                               "Category",
//                               "Item Condition",
//                               "Status"
//                             ]}
//                             fileName="AgentInventory"
//                           >
//                             {(props) => <button {...props} className="btn btn-success">Export to Excel</button>}
//                           </ExportAsExcel> */}
//                 </div>

//               </div>
//               <div style={{ overflowX: 'auto' }}>
//                 <MuiTable
//                   className="datatable"
//                   style={{ tableLayout: "fixed" }}
//                   sx={{
//                     borderCollapse: "collapse",
//                     '& th': {
//                       position: 'relative',
//                       userSelect: 'none',
//                       borderRight: 'none !important',
//                     },
//                   }}
//                 >
//                   <TableHead>
//                     {agentInventoryTable.getHeaderGroups().map(headerGroup => (
//                       <TableRow key={headerGroup.id}>
//                         {headerGroup.headers.map(header => (
//                           <TableCell
//                             key={header.id}
//                             style={{
//                               width: header.getSize(),
//                               minWidth: header.getSize(),
//                               maxWidth: header.getSize(),
//                               padding: "8px",
//                             }}
//                           >
//                             {!header.isPlaceholder &&
//                               flexRender(header.column.columnDef.header, header.getContext())}
//                             {header.column.getCanResize() && (
//                               <div
//                               // {...{
//                               //   onMouseDown: header.getResizeHandler(),
//                               //   onTouchStart: header.getResizeHandler(),
//                               //   style: {
//                               //     position: "absolute",
//                               //     right: 0,
//                               //     top: "25%",
//                               //     height: "50%",
//                               //     width: "4px",
//                               //     cursor: "col-resize",
//                               //     userSelect: "none",
//                               //     touchAction: "none",
//                               //     backgroundColor: "#aaa", // always visible gray bar
//                               //     borderRadius: "2px",
//                               //     zIndex: 10,
//                               //     transition: "background-color 0.2s",
//                               //   },
//                               //   className: header.column.getIsResizing() ? "isResizing" : "",
//                               // }}
//                               />
//                             )}
//                           </TableCell>
//                         ))}
//                       </TableRow>
//                     ))}
//                   </TableHead>
//                   <TableBody>
//                     {agentInventoryTable.getRowModel().rows.map(row => (
//                       <TableRow key={row.id}>
//                         {row.getVisibleCells().map(cell => (
//                           <TableCell
//                             key={cell.id}
//                             style={{
//                               width: cell.column.getSize(),
//                               minWidth: cell.column.getSize(),
//                               maxWidth: cell.column.getSize(),
//                               padding: "8px",
//                               borderRight: "none",
//                               borderLeft: "none",
//                             }}
//                           >
//                             {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                           </TableCell>
//                         ))}
//                       </TableRow>
//                     ))}
//                   </TableBody>
//                 </MuiTable>



//               </div>
//               {/* <table className="table datatable">
//                 <thead>
//                   {agentInventoryTable.getHeaderGroups()?.map(headerGroup => (
//                     <tr key={headerGroup.id}>
//                       {headerGroup.headers?.map(header => (
//                         <th key={header.id}>
//                           {flexRender(header.column.columnDef.header, header.getContext())}
//                         </th>
//                       ))}
//                     </tr>
//                   ))}
//                 </thead>
//                 <tbody>
//                   {agentInventoryTable.getRowModel().rows?.map(row => (
//                     <tr key={row.id}>
//                       {row.getVisibleCells()?.map(cell => (
//                         <td key={cell.id}>
//                           {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                         </td>
//                       ))}
//                     </tr>
//                   ))}
//                 </tbody>
//               </table> */}
//             </div>
//             {/* Pagination */}
//             <div className="d-flex justify-content-between align-items-center mt-3 pagination-blk">
//               <span>
//                 Page {agentInventoryTable.getState().pagination.pageIndex + 1} of {agentInventoryTable.getPageCount()}
//               </span>

//               <div className="pagination-container">
//                 {/* ◀️ Left */}
//                 <button
//                   className="pagination-arrow"
//                   onClick={() => agentInventoryTable.previousPage()}
//                   disabled={!agentInventoryTable.getCanPreviousPage()}
//                 >
//                   <i className="ri-arrow-left-circle-line fs-24"></i>
//                 </button>

//                 {/* 1 */}
//                 <button
//                   className={`pagination-circle${agentInventoryCurrentPage === 0 ? ' active' : ''}`}
//                   onClick={() => agentInventoryTable.setPageIndex(0)}
//                 >
//                   1
//                 </button>

//                 {/* … before */}
//                 {agentInventoryCurrentPage > 1 && <span className="pagination-ellipsis">…</span>}

//                 {/* current */}
//                 {agentInventoryCurrentPage > 0 && agentInventoryCurrentPage < agentInventoryPageCount - 1 && (
//                   <button
//                     className="pagination-circle active"
//                     onClick={() => agentInventoryTable.setPageIndex(agentInventoryCurrentPage)}
//                   >
//                     {agentInventoryCurrentPage + 1}
//                   </button>
//                 )}

//                 {/* … after */}
//                 {agentInventoryCurrentPage < agentInventoryPageCount - 2 && <span className="pagination-ellipsis">…</span>}

//                 {/* last */}
//                 {agentInventoryPageCount > 1 && (
//                   <button
//                     className={`pagination-circle${agentInventoryCurrentPage === agentInventoryPageCount - 1 ? ' active' : ''}`}
//                     onClick={() => agentInventoryTable.setPageIndex(agentInventoryPageCount - 1)}
//                   >
//                     {agentInventoryPageCount}
//                   </button>
//                 )}

//                 {/* ▶️ Right */}
//                 <button
//                   className="pagination-arrow"
//                   onClick={() => agentInventoryTable.nextPage()}
//                   disabled={!agentInventoryTable.getCanNextPage()}
//                 >
//                   <i className="ri-arrow-right-circle-line fs-24"></i>
//                 </button>
//               </div>

//               {/* <div className="btn-group">
//                     <button
//                       className={`btn btn-sm ${agentInventoryCurrentPage === 0 ? 'disabled' : ''}`}
//                       onClick={() => agentInventoryTable.previousPage()}
//                       disabled={!agentInventoryTable.getCanPreviousPage()}
//                     >
//                       <i className="ri-arrow-left-s-line fs-20"></i>
//                     </button>
//                     <button
//                       className={`btn btn-sm ${agentInventoryCurrentPage === 0 ? 'btn-success' : 'btn-outline-secondary'}`}
//                       onClick={() => agentInventoryTable.setPageIndex(0)}
//                     >
//                       1
//                     </button>

//                     {agentInventoryCurrentPage > 1 && (
//                       <button className="btn btn-sm" disabled>
//                         ...
//                       </button>
//                     )}

//                     {agentInventoryCurrentPage !== 0 && agentInventoryCurrentPage !== agentInventoryPageCount - 1 && (
//                       <button
//                         className="btn btn-sm btn-success"
//                         onClick={() => agentInventoryTable.setPageIndex(agentInventoryCurrentPage)}
//                       >
//                         {agentInventoryCurrentPage + 1}
//                       </button>
//                     )}

//                     {agentInventoryCurrentPage < agentInventoryPageCount - 2 && (
//                       <button className="btn btn-sm" disabled>
//                         ...
//                       </button>
//                     )}

//                     {agentInventoryPageCount > 1 && (
//                       <button
//                         className={`btn btn-sm ${agentInventoryCurrentPage === agentInventoryPageCount - 1 ? 'btn-success' : 'btn-outline-secondary'}`}
//                         onClick={() => agentInventoryTable.setPageIndex(agentInventoryPageCount - 1)}
//                       >
//                         {agentInventoryPageCount}
//                       </button>
//                     )}

//                     <button
//                       className={`btn btn-sm ${agentInventoryCurrentPage === agentInventoryPageCount - 1 ? 'disabled' : ''}`}
//                       onClick={() => agentInventoryTable.nextPage()}
//                       disabled={!agentInventoryTable.getCanNextPage()}
//                     >
//                       <i className="ri-arrow-right-s-line fs-22"></i>
//                     </button>
//                   </div> */}

//               <div className="d-flex align-items-center gap-2">
//                 <label htmlFor="pageSizeSelectAgentInventory">Rows per page:</label>
//                 <select
//                   id="pageSizeSelectAgentInventory"
//                   className="form-select pageSizeSelect"
//                   value={agentInventoryTable.getState().pagination.pageSize}
//                   onChange={(e) => {
//                     agentInventoryTable.setPageSize(Number(e.target.value));
//                   }}
//                 >
//                   {[5, 10, 20, 50, 100].map((size) => (
//                     <option key={size} value={size}>
//                       {size}
//                     </option>
//                   ))}
//                 </select>
//               </div>
//             </div>
//           </div>
//           <div className={`tab-pane ${activeTab === "#orders" ? "active" : ""}`} id="orders" role="tabpanel">
//             <div className=" mt-4 card-bg-fill">
//               <div className="" >
//                 <div className="table-responsive table-card mb-1" style={{ overflowX: 'auto' }}>
//                   <table
//                     className="datatable table table-nowrap align-middle"
//                     id="orderTable"
//                   >
//                     <thead>
//                       <tr>
//                         <th className="table-headers text-center" style={{ width: '5%' }}>S.No</th>
//                         <th className="table-headers text-center" style={{ width: '10%' }}>Order ID</th>
//                         <th className="table-headers text-center" style={{ width: '15%' }}>Customer</th>
//                         <th className="table-headers" style={{ width: '15%' }}>Product</th>
//                         <th className="table-headers text-center" style={{ width: '7%' }}>Order Date</th>
//                         <th className="table-headers text-center" style={{ width: '5%' }}>Amount</th>
//                         <th className="table-headers text-center" style={{ width: '10%' }}>Payment Method</th>
//                         <th className="table-headers text-center" style={{ width: '10%' }}>Production Status</th>
//                         <th className="table-headers text-center" style={{ width: '8%' }}>Order Status</th>
//                         <th className="table-headers text-center" style={{ width: '8%' }}>Payment Status</th>
//                         <th className="table-headers text-center" style={{ width: '8%' }}>Status</th>
//                         <th className="table-headers text-center" style={{ width: '5%' }}>Action</th>
//                       </tr>
//                     </thead>
//                     <tbody className="list form-check-all">
//                       {orders != null && orders?.map((order, index) => (
//                         <tr key={order.id}>
//                           <td className="table-cells text-center">{index + 1}</td>
//                           <td className="table-cells text-center">
//                             <a
//                               href={`/signage/orderDetails/${order.id}`}
//                               className="fw-medium link-primary"
//                             >
//                               {order.order_number}
//                             </a>
//                           </td>
//                           <td className="table-cells text-center">{order.customer_first_name} {order.customer_last_name}</td>
//                           <td className="table-cells">{order.items[0].product_name}</td>
//                           <td className="table-cells text-center">
//                             {order.created_at ? (new Date(order.created_at).toLocaleDateString("en-GB")) : "---"}
//                           </td>
//                           <td className="table-cells text-center">{order.total_amount}</td>
//                           <td className="table-cells text-center">{order.payment_method}</td>
//                           <td className="table-cells text-center">{order.items[0].production_status || "-"}</td>
//                           <td className="table-cells text-center">
//                             <SalesOrderStatusBadge value={order.order_status} type="order" />
//                             {/* {order.order_status === 100 ? <span className="status-badge status-pending-btn">Draft</span> :
//                             order.order_status === 101 ? <span className="status-badge status-accepted-btn">Confirmed</span> :
//                               order.order_status === 300 ? <span className="status-badge status-shipped-btn">Shipping</span> :
//                                 order.order_status === 301 ? <span className="status-badge status-shipped-btn">Shipped</span> :
//                                   order.order_status === 302 || order.order_status === 401 ? <span className="status-badge status-delivered-btn">Delivered</span> :
//                                     order.order_status === 400 ? <span className="status-badge status-canceled-btn">Cancelled</span> : "-"} */}

//                           </td>
//                           <td className="table-cells text-center">
//                             <span className="">
//                               <SalesOrderStatusBadge value={order.payment_status} type="payment" />
//                               {/* {order.payment_status === "Partial Payment" ? <span className="status-badge status-pending-btn">Partial Payment</span> :
//                                 order.payment_status === "Paid" ? <span className="status-badge status-accepted-btn">Paid</span> :
//                                   order.payment_status === "Pending" ? <span className="status-badge status-pending-btn">Pending</span> : "-"} */}
//                             </span>
//                           </td>
//                           <td className="table-cells text-center">
//                             <input
//                               type="radio"
//                               className={
//                                 order.order_status
//                                   ? "status-radio status-radio-active"
//                                   : "status-radio status-radio-inactive"
//                               }
//                               checked
//                               readOnly
//                             />
//                             {/*                               

//                             {order?.order_status ? <span className="status-badge status-accepted-btn">Active</span> : <span className="status-badge status-draft-btn">Inactive</span>} */}
//                           </td>
//                           <td className="table-cells text-center">
//                             <ul className="list-inline hstack gap-2 mb-0 justify-content-center">
//                               <li
//                                 className="list-inline-item"
//                                 data-bs-toggle="tooltip"
//                                 data-bs-trigger="hover"
//                                 data-bs-placement="top"
//                                 title="View"
//                               >
//                                 <a
//                                   href={`/signage/orderDetails/${order.id}`}
//                                   className="text-primary d-inline-block"
//                                 >
//                                   <i className="ri-eye-fill fs-16"></i>
//                                 </a>
//                               </li>
//                               {/* Additional actions can go here */}
//                             </ul>
//                           </td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                   <div className="noresult" style={{ display: "none" }}>
//                     <div className="text-center">
//                       <h5 className="mt-2">Sorry! No Result Found</h5>
//                       <p>
//                         We've searched more than 150+ Orders. We did not find any orders for your search.
//                       </p>
//                     </div>
//                   </div>
//                 </div>

//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   </div>
// );


















{/* <div className="card">
        <div className="card-body">
          <h2 className="d-flex justify-content-center">Agent Details</h2>
          <hr className="row"></hr>
          <div className="row justify-content-center">
            <div className="col-md-12 col-lg-12 col-xl-12">
              <div>
                <div > */}
{/* <div className="d-flex align-items-center border-bottom pb-3 mb-4 flex-wrap">
                    <div
                      className="rounded-circle d-flex justify-content-center align-items-center me-4 profile"
                    >
                      {teammember?.firstName?.[0]}
                      {teammember?.lastName?.[0]}
                    </div>
                    <div>
                      <h4 className="mb-1">
                        {teammember?.firstName} {teammember?.lastName}
                      </h4>
                      <p className="mb-1 text-muted">{teammember?.jobTitle}</p>
                      <p className="mb-0 text-muted">California, United States</p>
                    </div>
                  </div> */}
//         <div className="row g-4">
//           <div className="col-md-4">
//             <div className=" d-flex">
//               <label className="form-label fw-semibold mb-0 me-2">First Name:</label>
//               <div>{agent?.firstName}</div>
//             </div>
//           </div>
//           <div className="col-md-4">
//             <div className=" d-flex">
//               <label className="form-label fw-semibold mb-0 me-2">Last Name:</label>
//               <div>{agent?.lastName}</div>
//             </div>
//           </div>
//           <div className="col-md-4">
//             <div className=" d-flex">
//               <label className="form-label fw-semibold mb-0 me-2">Email:</label>
//               <div>{agent?.email}</div>
//             </div>
//           </div>
//           <hr className="row"></hr>
//           <div className="col-md-4">
//             <div className=" d-flex">
//               <label className="form-label fw-semibold mb-0 me-2">Phone:</label>
//               <div>{agent?.phone1}</div>
//             </div>
//           </div>
//           <div className="col-md-4">
//             <div className=" d-flex">
//               <label className="form-label fw-semibold mb-0 me-2">Role:</label>
//               <div>Agent</div>
//             </div>
//           </div>
//           <div className="col-md-4">
//             <div className=" d-flex">
//               <label className="form-label fw-semibold mb-0 me-2">Location:</label>
//               <div>{
//                 agent?.firmDetails?.address?.addressLine1
//               }
//                 ,
//                 {
//                   agent?.firmDetails?.address?.addressLine2
//                 }
//                 ,{agent?.firmDetails?.address?.city},
//                 {agent?.firmDetails?.address?.state},
//                 {agent?.firmDetails?.address?.zip}</div>
//             </div>
//           </div>
//           <hr className="row"></hr>
//           <div className="col-md-4">
//             <div className=" d-flex">
//               <label className="form-label fw-semibold mb-0 me-2 ">  Brokerage Firm Affiliation :</label>
//               <div> {
//                 agent?.agentDetails?.brokerageFirmAffiliation
//               }</div>
//             </div>
//           </div>
//           <div className="col-md-4">
//             <div className=" d-flex">
//               <label className="form-label fw-semibold mb-0 me-2 "> Agent ID/Code :</label>
//               <div> {agent?.agentDetails?.agentID}</div>
//             </div>
//           </div>
//           <div className="col-md-4">
//             <div className=" d-flex">
//               <label className="form-label fw-semibold mb-0 me-2 ">Status:</label>
//               <div>{agent?.status}</div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   </div>
// </div>
// <div className="mb-lg-3 profile-wrapper">
//   <div className="row g-4">
{/* <div className="col-auto">
                <div className="avatar-lg">
                  { teammember?.profilePic ? (
                    <img
                      src={`https://dev.reelty.app/${teammember?.profilePic}`}
                      alt="user-img"
                      className="img-thumbnail rounded-circle"
                    />
                  ): (
                  <img
                    src="/assets/images/users/avatar-1.jpg"
                    alt="user-img"
                    className="img-thumbnail rounded-circle"
                  />
                  )}
                  
                </div>
              </div> */}

//       </div>
//     </div>

//   </div>
// </div>